import { IApiError, IApiErrorWithErrors } from "@/interface/apiError.types";
import { showToast, TOAST_TYPES } from "../toast-utils/toast.util";

type ErrorResponse = {
  error: string | string[];
};

const extractErrorMessages = (errorData: IApiErrorWithErrors): string[] => {
  if (errorData.errors && Array.isArray(errorData.errors)) {
    return errorData.errors.map(
      (err: { field: string; message: string | string[] }) =>
        Array.isArray(err.message) ? err.message.join(", ") : err.message
    );
  } else if (
    errorData.errors &&
    typeof errorData.errors === "object" &&
    errorData.errors !== null
  ) {
    return Object.values(errorData.errors).flat().map(String);
  } else {
    return [errorData.message];
  }
};

export const parseApiErrorWithoutToast = (error: IApiError): ErrorResponse => {
  if (!error) return { error: "Something went wrong" };

  if (error.code === "ERR_CANCELED") {
    return { error: error?.response?.data?.message ?? "Request canceled" };
  }

  if (error.response?.data?.message) {
    return { error: error?.response?.data?.message };
  }

  if (error.request?.data?.message) {
    return { error: error?.request?.data?.message };
  }

  return { error: "Something went wrong" };
};

export const parseApiError = async (
  error: IApiError
): Promise<ErrorResponse> => {
  if (!error) return { error: "Something went wrong" };

  if (error.code === "ERR_CANCELED") {
    const message = error?.response?.data?.message ?? "Request canceled";

    // Check if cancellation was due to timeout
    if (error.message?.includes("timeout")) {
      showToast(TOAST_TYPES.error, "Request timed out");
      return { error: "Request timed out" };
    }

    // AbortController or other cancellation - silent handling
    return { error: message };
  }

  if (error.code === "ERR_NETWORK") {
    const message = error.message ?? "Network error";
    showToast(TOAST_TYPES.error, message);
    return { error: message };
  }

  let errorMessages: string[] = [];

  if (error.response?.data) {
    errorMessages = extractErrorMessages(error.response.data);

    if (!errorMessages.length) {
      errorMessages.push(
        `${error.response.status} ${error.response.statusText}`
      );
    }
  } else if (error.request?.data?.message) {
    errorMessages.push(error.request.data.message);
  }

  if (!errorMessages.length) {
    errorMessages.push("Something went wrong");
  }

  errorMessages.forEach(async (message) => {
    showToast(TOAST_TYPES.error, message);
  });

  return { error: errorMessages };
};
